import React, { useMemo, useState } from 'react';
import AssessmentPortalContext from './context/assessment-portal-context';
import type { AssessmentPortalContextValue } from './context/assessment-portal-context';
import PortalHeader from './components/portal-header';
import StepTracker from './components/step-tracker';
import useAssessmentPortalStepPageView from './hooks/use-assessment-portal-step-page-view';
import useAssessmentPortalStepTitle from './hooks/use-assessment-portal-step-title';
import useStepRouteSync from './hooks/use-step-route-sync';
import useStepRouteInit from './hooks/use-step-route-init';
import useUnloadWarningUntilStep from './hooks/use-unload-warning-until-step';
import useScrollToTopOnChange from './hooks/use-scroll-to-top-on-change';
import type { AssessmentPortalConfig } from './types/config';

interface AssessmentPortalAppProps<T extends Record<string, any> = Record<string, any>> {
  config: AssessmentPortalConfig<T>;
}

function AssessmentPortalApp<T extends Record<string, any> = Record<string, any>>(
  { config }: AssessmentPortalAppProps<T>,
) {
  const initialStepIndex = useStepRouteInit(
    config.basePath,
    config.flowSteps,
    config.reloadReset,
    config.resolveInitialStepIndex,
  );
  const [currentStepIndex, setCurrentStepIndex] = useState(initialStepIndex);
  const [flowState, setFlowState] = useState<T>(
    config.createInitialFlowState(),
  );

  const stepPaths = useMemo(
    () => config.flowSteps.reduce<Record<number, string>>(
      (acc, step, idx) => {
        acc[idx] = `${config.basePath}${step.path}`;
        return acc;
      },
      {},
    ),
    [config.flowSteps, config.basePath],
  );

  useStepRouteSync(currentStepIndex, stepPaths, config.basePath);
  useScrollToTopOnChange(currentStepIndex);

  useUnloadWarningUntilStep(
    config.flowSteps,
    currentStepIndex,
    config.unloadWarningUntilReachedStepKey,
  );

  const currentStep = config.flowSteps[currentStepIndex];
  const currentStepLabel = config.flowSteps[currentStepIndex]?.label;

  const analyticsStepLabel = currentStep && config.resolveStepAnalyticsLabel
    ? config.resolveStepAnalyticsLabel(currentStep, flowState)
    : currentStepLabel;

  useAssessmentPortalStepTitle(currentStepLabel);
  useAssessmentPortalStepPageView({
    currentStepIndex,
    stepPaths,
    basePath: config.basePath,
    stepLabel: analyticsStepLabel,
  });

  const trackerLabels = useMemo(
    () => Array.from(new Set(
      config.flowSteps
        .filter((step) => step.trackerLabel)
        .map((step) => step.trackerLabel!),
    )),
    [config.flowSteps],
  );

  const trackerStepIndex = useMemo(() => {
    if (!currentStep?.trackerLabel) return -1;
    return trackerLabels.indexOf(currentStep.trackerLabel);
  }, [currentStep, trackerLabels]);

  const updateFlowState = (key: string, value: any) => {
    setFlowState((previous) => ({ ...previous, [key]: value } as T));
  };

  const goToStep = (stepKey: string) => {
    const index = config.flowSteps.findIndex((s) => s.key === stepKey);
    if (index !== -1) {
      setCurrentStepIndex(index);
    }
  };

  const handleNextStep = () => {
    if (currentStepIndex < config.flowSteps.length - 1) {
      setCurrentStepIndex((i) => i + 1);
    }
  };

  const handlePreviousStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((i) => i - 1);
    }
  };

  const handleStepComplete = (stepKey: string, values: any) => {
    setFlowState((previous) => ({ ...previous, [stepKey]: values } as T));
    handleNextStep();
  };

  if (!currentStep) return null;

  const StepComponent = currentStep.component;
  const showTracker = currentStep.layout === 'with-tracker';

  const HeaderComponent = config.componentOverrides?.PortalHeader || PortalHeader;
  const TrackerComponent = config.componentOverrides?.StepTracker || StepTracker;

  const themeStyle = config.theme?.cssVariables
    ? Object.entries(config.theme.cssVariables)
      .reduce<Record<string, string>>(
        (acc, [key, value]) => ({ ...acc, [key]: value }),
        {},
      )
    : undefined;

  const contextValue: AssessmentPortalContextValue<any> = {
    config,
    flowState,
    updateFlowState,
    goToStep,
    currentStepIndex,
  };

  const stepElement = (
    <StepComponent
      defaultValues={(flowState as Record<string, any>)[currentStep.key]}
      onBack={handlePreviousStep}
      onNext={(values) => handleStepComplete(currentStep.key, values)}
    />
  );

  const mobileHeaderTitle = showTracker ? currentStep.mobileTitle : undefined;
  const mobileHeaderSubtitle = showTracker ? currentStep.mobileSubtitle : undefined;
  const mobileHeaderChip = showTracker && trackerStepIndex >= 0
    ? `Step ${trackerStepIndex + 1} of ${trackerLabels.length}`
    : undefined;
  const mobileHeaderProgress = showTracker && trackerStepIndex >= 0
    ? ((trackerStepIndex + 1) / trackerLabels.length) * 100
    : undefined;
  const mobileHeaderOnBack = showTracker && currentStepIndex > 0
    ? () => {
      config.onHeaderBack?.(currentStep);
      handlePreviousStep();
    }
    : undefined;

  return (
    <AssessmentPortalContext.Provider value={contextValue}>
      <div
        className={`assessment-portal min-h-screen flex flex-col bg-page ${config.theme?.cssClass ?? ''}`}
        style={themeStyle}
      >
        {showTracker ? (
          <>
            <HeaderComponent
              title={mobileHeaderTitle}
              subtitle={mobileHeaderSubtitle}
              chip={mobileHeaderChip}
              progress={mobileHeaderProgress}
              onBack={mobileHeaderOnBack}
            />
            {trackerStepIndex >= 0 && (
              <TrackerComponent current={trackerStepIndex} labels={trackerLabels} />
            )}
            <div className="flex-1 px-0 py-0 pb-0 md:px-4 md:py-8 md:pb-8">
              {stepElement}
            </div>
          </>
        ) : (
          stepElement
        )}
      </div>
    </AssessmentPortalContext.Provider>
  );
}

export default AssessmentPortalApp;
